import React, { useEffect, useState } from 'react';
import { Button, Input, Modal, Table, Badge } from '../../components/common';
import { userGroupService } from '../../services/resources';
import { apiError } from '../../services/api';
import { groupLabel } from '../../config/forms';
import { useAuth } from '../../context/AuthContext';

const PERMS = ['CanView', 'CanCreate', 'CanEdit', 'CanDelete'];
const PERM_LABELS = { CanView: 'View', CanCreate: 'Create', CanEdit: 'Edit', CanDelete: 'Delete' };

export default function UserGroupsTab({ onToast }) {
  const { can } = useAuth();
  const canCreate = can('SETTINGS', 'CanCreate');
  const canEdit = can('SETTINGS', 'CanEdit');
  const canDelete = can('SETTINGS', 'CanDelete');

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  // group create/edit modal
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ GroupName: '', Description: '', IsActive: true });
  const [error, setError] = useState('');

  // matrix
  const [matrix, setMatrix] = useState([]);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [savingMatrix, setSavingMatrix] = useState(false);

  const loadGroups = async () => {
    setLoading(true);
    try {
      setGroups(await userGroupService.list());
    } catch (err) {
      onToast?.(apiError(err).message, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGroups();
  }, []); // eslint-disable-line

  const selectGroup = async (group) => {
    setSelected(group);
    setMatrixLoading(true);
    try {
      setMatrix(await userGroupService.getPermissions(group.GroupID));
    } catch (err) {
      onToast?.(apiError(err).message, 'error');
    } finally {
      setMatrixLoading(false);
    }
  };

  const toggle = (formId, perm) =>
    setMatrix((prev) =>
      prev.map((r) => (r.FormID === formId ? { ...r, [perm]: !r[perm] } : r))
    );

  const toggleAllForForm = (formId, value) =>
    setMatrix((prev) =>
      prev.map((r) =>
        r.FormID === formId ? { ...r, CanView: value, CanCreate: value, CanEdit: value, CanDelete: value } : r
      )
    );

  const saveMatrix = async () => {
    setSavingMatrix(true);
    try {
      await userGroupService.savePermissions(selected.GroupID, matrix);
      onToast?.('Permissions saved');
    } catch (err) {
      onToast?.(apiError(err).message, 'error');
    } finally {
      setSavingMatrix(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ GroupName: '', Description: '', IsActive: true });
    setError('');
    setOpen(true);
  };
  const openEdit = (g) => {
    setEditing(g);
    setForm({ GroupName: g.GroupName, Description: g.Description || '', IsActive: !!g.IsActive });
    setError('');
    setOpen(true);
  };

  const removeGroup = async (g) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete group "${g.GroupName}"?`)) return;
    try {
      await userGroupService.remove(g.GroupID);
      onToast?.('Group deleted');
      if (selected && selected.GroupID === g.GroupID) {
        setSelected(null);
        setMatrix([]);
      }
      loadGroups();
    } catch (err) {
      onToast?.(apiError(err).message, 'error');
    }
  };

  const submitGroup = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editing) {
        await userGroupService.update(editing.GroupID, form);
        onToast?.('Group updated');
      } else {
        await userGroupService.create(form);
        onToast?.('Group created');
      }
      setOpen(false);
      loadGroups();
    } catch (err) {
      setError(apiError(err).message);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      {/* Groups list */}
      <div>
        {canCreate && (
          <div className="mb-3 flex justify-end">
            <Button onClick={openCreate}>+ New Group</Button>
          </div>
        )}
        <Table
          loading={loading}
          rows={groups}
          columns={[
            { key: 'GroupName', label: 'Group' },
            {
              key: 'IsActive',
              label: 'Status',
              render: (r) => (r.IsActive ? <Badge tone="green">Active</Badge> : <Badge tone="gray">Off</Badge>),
            },
            {
              key: '__a',
              label: 'Actions',
              className: 'text-right',
              render: (r) => (
                <div className="flex justify-end gap-1.5">
                  <Button size="sm" onClick={() => selectGroup(r)}>
                    Permissions
                  </Button>
                  {canEdit && (
                    <Button size="sm" variant="secondary" onClick={() => openEdit(r)}>
                      Edit
                    </Button>
                  )}
                  {canDelete && (
                    <Button size="sm" variant="danger" onClick={() => removeGroup(r)}>
                      Delete
                    </Button>
                  )}
                </div>
              ),
            },
          ]}
        />
      </div>

      {/* Permission matrix */}
      <div>
        {!selected ? (
          <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-gray-300 p-8 text-sm text-gray-400">
            Select a group to edit its permissions
          </div>
        ) : (
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-800">
                Permissions — {selected.GroupName}
              </h3>
              {canEdit && (
                <Button size="sm" onClick={saveMatrix} disabled={savingMatrix || matrixLoading}>
                  {savingMatrix ? 'Saving…' : 'Save'}
                </Button>
              )}
            </div>
            {matrixLoading ? (
              <p className="text-sm text-gray-400">Loading…</p>
            ) : (
              <div className="max-h-[55vh] overflow-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500">
                      <th className="sticky top-0 z-10 bg-white py-2 pr-3">Form</th>
                      {PERMS.map((p) => (
                        <th key={p} className="sticky top-0 z-10 bg-white px-2 py-2 text-center">
                          {PERM_LABELS[p]}
                        </th>
                      ))}
                      <th className="sticky top-0 z-10 bg-white px-2 py-2 text-center">All</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {matrix.map((row) => {
                      const all = PERMS.every((p) => row[p]);
                      return (
                        <tr key={row.FormID}>
                          <td className="py-2 pr-3">
                            <div className="font-medium text-gray-700">{row.FormName}</div>
                            {row.ParentGroup && (
                              <div className="text-xs text-gray-400">{groupLabel(row.ParentGroup)}</div>
                            )}
                          </td>
                          {PERMS.map((p) => (
                            <td key={p} className="px-2 py-2 text-center">
                              <input
                                type="checkbox"
                                checked={!!row[p]}
                                disabled={!canEdit}
                                onChange={() => toggle(row.FormID, p)}
                              />
                            </td>
                          ))}
                          <td className="px-2 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={all}
                              disabled={!canEdit}
                              onChange={(e) => toggleAllForForm(row.FormID, e.target.checked)}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Group create/edit modal */}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? 'Edit Group' : 'New Group'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submitGroup}>Save</Button>
          </>
        }
      >
        <form onSubmit={submitGroup} className="space-y-3">
          {error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
          <Input
            label="Group Name"
            required
            value={form.GroupName}
            onChange={(e) => setForm({ ...form, GroupName: e.target.value })}
          />
          <Input
            label="Description"
            value={form.Description}
            onChange={(e) => setForm({ ...form, Description: e.target.value })}
          />
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form.IsActive}
              onChange={(e) => setForm({ ...form, IsActive: e.target.checked })}
            />
            Active
          </label>
        </form>
      </Modal>
    </div>
  );
}
